// core/registry.ts — Platik-IA
// Orquestador DAG: registro, resolución de dependencias y ciclo de vida.

import { bus } from './event-bus';

type ModuleInit = () => Promise<unknown>;
type ModuleDef  = { name: string; deps: string[]; init: ModuleInit };

const registry  = new Map<string, ModuleDef>();
const instances = new Map<string, unknown>();
const disposes  = new Map<string, () => void>();

export function register(name: string, deps: string[], init: ModuleInit): void {
  if (registry.has(name)) throw new Error(`[registry] Módulo "${name}" ya registrado`);
  registry.set(name, { name, deps, init });
}

export function unregister(name: string): void {
  const dispose = disposes.get(name);
  if (dispose) {
    try { dispose(); } catch (e) { console.warn(`[registry] Error en dispose de "${name}":`, e); }
  }
  registry.delete(name);
  instances.delete(name);
  disposes.delete(name);
  bus.emit('nav:module-status', { name, available: false });
}

export async function boot(): Promise<void> {
  const ordered = resolveOrder(registry);

  for (const mod of ordered) {
    const missingDep = mod.deps.find(dep => !instances.has(dep));
    if (missingDep) {
      const err = new Error(`[registry] "${mod.name}" requiere "${missingDep}" que no está disponible`);
      bus.emit('module:error', { name: mod.name, error: err });
      console.error(err.message);
      continue;
    }

    try {
      const api = await mod.init() as Record<string, unknown>;
      instances.set(mod.name, api);
      if (typeof api?.dispose === 'function') {
        disposes.set(mod.name, api.dispose as () => void);
      }
      bus.emit('module:ready', { name: mod.name });
      bus.emit('nav:module-status', { name: mod.name, available: true });
    } catch (err) {
      bus.emit('module:error', { name: mod.name, error: err as Error });
      console.error(`[registry] Error inicializando "${mod.name}":`, err);
    }
  }

  bus.emit('app:ready');
}

export function getModule<T>(name: string): T | null {
  return (instances.get(name) as T) ?? null;
}

function resolveOrder(map: Map<string, ModuleDef>): ModuleDef[] {
  const visited = new Set<string>();
  const result: ModuleDef[] = [];

  function visit(name: string) {
    if (visited.has(name)) return;
    visited.add(name);
    const mod = map.get(name);
    if (!mod) return;
    for (const dep of mod.deps) visit(dep);
    result.push(mod);
  }

  for (const name of map.keys()) visit(name);
  return result;
}
